import { Box, Typography, Alert, Divider } from "@mui/material";
import type {
  JobType,
  BookingReconciliationResultDto,
  AssignContractLinesResultDto,
  EligibleInvoicesResultDto,
  CurrencyRateSyncResultDto,
} from "@/types/jobs";

interface Props {
  jobType: JobType;
  parsedResult: unknown;
}

function CounterRow({ items }: { items: { label: string; value: number | string }[] }) {
  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1 }}>
      {items.map(({ label, value }) => (
        <Box key={label} sx={{ textAlign: "center", minWidth: 72 }}>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function BookingReconciliationResult({ result }: { result: BookingReconciliationResultDto }) {
  return (
    <Box>
      <CounterRow
        items={[
          { label: "Conciliados", value: result.TotalMatched ?? "—" },
          { label: "Pend. revisión", value: result.TotalPendingReview ?? "—" },
          { label: "Sin match", value: result.TotalUnmatched ?? "—" },
        ]}
      />
      {(result.TotalPendingReview ?? 0) > 0 && (
        <Alert severity="warning" sx={{ py: 0.5, mb: 1, borderRadius: "8px" }}>
          <Typography variant="caption">{result.TotalPendingReview} booking(s) requieren revisión manual.</Typography>
        </Alert>
      )}
      {result.Message && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>{result.Message}</Typography>
      )}
    </Box>
  );
}

function ContractAssignmentResult({ result }: { result: AssignContractLinesResultDto }) {
  return (
    <CounterRow
      items={[
        { label: "Bookings", value: result.TotalBookingsProcessed ?? "—" },
        { label: "Equipos", value: result.TotalEquipmentsProcessed ?? "—" },
        { label: "Actualizados", value: result.TotalEquipmentsUpdated ?? "—" },
        { label: "Omitidos", value: result.TotalEquipmentsSkipped ?? "—" },
        { label: "Sin cambio", value: result.TotalEquipmentsUnchanged ?? "—" },
      ]}
    />
  );
}

function EligibleInvoicesResult({ result }: { result: EligibleInvoicesResultDto }) {
  const eligible = result.Eligible ?? [];
  const discarded = result.Discarded ?? [];

  return (
    <Box>
      <CounterRow
        items={[
          { label: "Analizadas", value: result.TotalAnalyzed ?? "—" },
          { label: "Elegibles", value: result.TotalEligible ?? "—" },
          { label: "Líneas eleg.", value: result.TotalEligibleLines ?? "—" },
          { label: "Descartadas", value: result.TotalDiscarded ?? "—" },
        ]}
      />
      {result.GeneratedBy && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, mb: 1 }}>
          {result.GeneratedBy} · {new Date(result.GeneratedAt).toLocaleString("es")}
        </Typography>
      )}
      <Divider sx={{ my: 1 }} />
      {eligible.length > 0 && (
        <Typography variant="caption" color="success.main" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
          {eligible.length} factura{eligible.length > 1 ? "s" : ""} elegible{eligible.length > 1 ? "s" : ""}
        </Typography>
      )}
      {discarded.length > 0 && (
        <Typography variant="caption" color="error.main" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
          {discarded.length} descartada{discarded.length > 1 ? "s" : ""}
        </Typography>
      )}
    </Box>
  );
}

function CurrencyRateSyncResult({ result }: { result: CurrencyRateSyncResultDto }) {
  return (
    <Box>
      <CounterRow
        items={[
          { label: "Tasas sincronizadas", value: result.RatesSynced ?? "—" },
        ]}
      />
      {result.SyncDate && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          {new Date(result.SyncDate).toLocaleString("es")}
        </Typography>
      )}
      {result.Message && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {result.Message}
        </Typography>
      )}
    </Box>
  );
}

function GenericResult({ parsedResult }: { parsedResult: unknown }) {
  if (parsedResult === undefined || parsedResult === null) return null;
  return (
    <Box component="pre" sx={{ fontSize: "0.72rem", bgcolor: "action.hover", borderRadius: "8px", p: 1.5, overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 240, overflowY: "auto", m: 0 }}>
      {JSON.stringify(parsedResult, null, 2)}
    </Box>
  );
}

export function JobResultRenderer({ jobType, parsedResult }: Props) {
  switch (jobType) {
    case "BookingReconciliation":
      return <BookingReconciliationResult result={parsedResult as BookingReconciliationResultDto} />;
    case "ContractAssignment":
      return <ContractAssignmentResult result={parsedResult as AssignContractLinesResultDto} />;
    case "EligibleInvoicesReport":
      return <EligibleInvoicesResult result={parsedResult as EligibleInvoicesResultDto} />;
    case "CurrencyRateSync":
      return <CurrencyRateSyncResult result={parsedResult as CurrencyRateSyncResultDto} />;
    default:
      return <GenericResult parsedResult={parsedResult} />;
  }
}
